import { api } from '../api';
import type { Asociado, AsociadosPage, InconsistenciasResponse, PadronSummary } from '../types';

export async function listPadrones(): Promise<PadronSummary[]> {
  const { data } = await api.get<PadronSummary[]>('/padrones');
  return data;
}

export async function getPadron(id: string): Promise<PadronSummary> {
  const { data } = await api.get<PadronSummary>(`/padrones/${id}`);
  return data;
}

export interface AsociadosQuery {
  estado?: 'ok' | 'error';
  /** Solo filas con advertencia (siguen en estado 'ok'). */
  conAdvertencia?: boolean;
  search?: string;
  legajo?: string;
  offset?: number;
  limit?: number;
}

export async function listAsociadosOfPadron(
  id: string,
  query: AsociadosQuery = {},
): Promise<AsociadosPage> {
  const { data } = await api.get<AsociadosPage>(`/padrones/${id}/asociados`, {
    params: query,
  });
  return data;
}

export async function getInconsistencias(id: string): Promise<InconsistenciasResponse> {
  const { data } = await api.get<InconsistenciasResponse>(`/padrones/${id}/inconsistencias`);
  return data;
}

/**
 * Headers/query con los que viaja la organización y el plan elegidos en la
 * pantalla. La organización va en `X-Empresa-Id` (el interceptor no lo pisa
 * si ya viene) y el plan por query en el upload directo / por body en el
 * confirm. Sin contexto, queda lo del selector global, como siempre.
 */
function conContexto(contexto?: ContextoCargaPadron): {
  headers: Record<string, string>;
  params: Record<string, string>;
} {
  return {
    headers: contexto?.empresaId ? { 'X-Empresa-Id': contexto.empresaId } : {},
    // La sucursal viaja SOLO junto con el plan: sola no identifica nada.
    params: contexto?.planCodigo
      ? {
          planCodigo: contexto.planCodigo,
          ...(contexto.sucursalId ? { sucursalId: contexto.sucursalId } : {}),
        }
      : {},
  };
}

export async function uploadPadron(
  file: File,
  contexto?: ContextoCargaPadron,
): Promise<PadronSummary & { asociados: Asociado[] }> {
  if (debeChunkear(file)) {
    return uploadChunked<PadronSummary & { asociados: Asociado[] }>(file, 'upload', contexto);
  }
  const form = new FormData();
  form.append('file', file);
  const ctx = conContexto(contexto);
  const { data } = await api.post('/uploads', form, {
    headers: { 'Content-Type': 'multipart/form-data', ...ctx.headers },
    params: ctx.params,
  });
  return data;
}

export interface PreviewCampo {
  key: string;
  label: string;
  requerido: boolean;
}

export interface PreviewResponse {
  uploadToken: string;
  filename: string;
  totalDataRows: number;
  headers: string[];
  detectedMapping: Record<string, number>;
  sampleRows: string[][];
  ignoradas: string[];
  desconocidas: string[];
  faltantes: string[];
  campos: PreviewCampo[];
}

/** Empresa y plan con los que entra el padrón (los elige la pantalla de carga). */
export interface ContextoCargaPadron {
  empresaId: string;
  planCodigo?: string;
  /**
   * Sucursal del plan elegido. Obligatoria para el server cuando el mismo plan
   * está en venta en varias sucursales (sin ella responde 400 y no adivina).
   */
  sucursalId?: string;
}

/**
 * El preview tiene que viajar con la MISMA organización que después va a ir
 * en el confirm: el server cachea el archivo por organización y rechaza el
 * confirm si difieren. Por eso recibe el contexto igual que `confirmPadron`.
 */
export async function previewPadron(
  file: File,
  contexto?: ContextoCargaPadron,
): Promise<PreviewResponse> {
  if (debeChunkear(file)) {
    return uploadChunked<PreviewResponse>(file, 'preview', contexto);
  }
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<PreviewResponse>('/uploads/preview', form, {
    headers: { 'Content-Type': 'multipart/form-data', ...conContexto(contexto).headers },
  });
  return data;
}

export async function confirmPadron(
  uploadToken: string,
  mapping: Record<string, number>,
  contexto?: ContextoCargaPadron,
): Promise<PadronSummary & { asociados: Asociado[] }> {
  // La empresa va en el header (como el resto de la app) y el plan en el body.
  // El server igual re-resuelve las dos cosas: esto es lo que se PIDE, no lo
  // que se aplica.
  const { data } = await api.post(
    '/uploads/confirm',
    {
      uploadToken,
      mapping,
      planCodigo: contexto?.planCodigo,
      sucursalId: contexto?.planCodigo ? contexto.sucursalId || undefined : undefined,
    },
    contexto?.empresaId ? { headers: { 'X-Empresa-Id': contexto.empresaId } } : undefined,
  );
  return data;
}

export interface CorreccionesResult {
  padron: PadronSummary;
  actualizadas: number;
  agregadas: number;
  omitidas: number;
}

export async function confirmCorrecciones(
  padronId: string,
  uploadToken: string,
  mapping: Record<string, number>,
  sobreescribirFilasCorrectas: boolean,
): Promise<CorreccionesResult> {
  const { data } = await api.post<CorreccionesResult>('/uploads/confirm-correcciones', {
    padronId,
    uploadToken,
    mapping,
    sobreescribirFilasCorrectas,
  });
  return data;
}

export interface ResumenPadron {
  totalLegajos: number;
  porEstado: Record<string, number>;
}

export interface ObservacionTitular {
  campo: string;
  asociadoId: string;
  mensaje: string;
  reportadoAt: string;
}

export interface ValidacionMedicus {
  accion: 'validar' | 'rechazar';
  motivo?: string;
  validadoPor: string;
  validadoAt: string;
}

export interface Legajo {
  id: string;
  empresaId: string;
  padronId: string;
  numero: string;
  titularAsociadoId: string;
  familiaresAsociadoIds: string[];
  estado: string;
  activacionToken: string;
  activacionExpiraAt: string;
  createdAt: string;
  observaciones?: ObservacionTitular[];
  validacionMedicus?: ValidacionMedicus;
  /**
   * El legajo quedó «Listo para alta» SIN asistente del titular (organización
   * con `sinWizard`): nadie hizo el onboarding ni la DDJJ.
   */
  validadoSinWizard?: { at: string; por: string };
}

export async function getResumenPadron(padronId: string): Promise<ResumenPadron> {
  const { data } = await api.get<ResumenPadron>(`/legajos/resumen/${padronId}`);
  return data;
}

export interface DesgloseEnValidacion {
  enDatosTitular: number;
  enDatosGF: number;
  enCuestionarioSalud: number;
  enEleccionPlan: number;
}

export interface DesgloseValidadoPorTitular {
  planMs1: number;
  planMs2: number;
  contactoComercial: number;
}

export interface PadronDashboard {
  padronId: string;
  totalLegajos: number;
  porEstado: Record<string, number>;
  desgloseEnValidacion: DesgloseEnValidacion;
  desgloseValidadoPorTitular: DesgloseValidadoPorTitular;
  observacionesPendientes: number;
  proximosAVencer: Array<{
    legajoId: string;
    numero: string;
    estado: string;
    expiraAt: string;
  }>;
  rechazosMedicus: Array<{ motivo: string; count: number }>;
  rechazosBinary: Array<{ motivo: string; count: number }>;
  renaper: {
    total: number;
    porEstado: {
      pendiente: number;
      validado: number;
      no_coincide: number;
      no_encontrado: number;
      error: number;
    };
    titularesConPisos: number;
  };
}

export async function getPadronDashboard(padronId: string): Promise<PadronDashboard> {
  const { data } = await api.get<PadronDashboard>(`/legajos/dashboard/${padronId}`);
  return data;
}

export interface DashboardLegajosGlobal {
  scope: { empresaIds: string[] | null };
  totalLegajos: number;
  porEstado: Record<string, number>;
  desgloseEnValidacion: DesgloseEnValidacion;
  desgloseValidadoPorTitular: DesgloseValidadoPorTitular;
  observacionesPendientes: number;
  preexistenciasDeclaradas: number;
  coberturaPrivadaDeclarada: number;

  // Conteos paralelos de PERSONAS (titular + familiares) por cada categoría
  // de legajos. La UI los muestra entre paréntesis junto al número de
  // legajos: "N legajos (M personas)".
  totalPersonas: number;
  personasPorEstado: Record<string, number>;
  desgloseEnValidacionPersonas: DesgloseEnValidacion;
  desgloseValidadoPorTitularPersonas: DesgloseValidadoPorTitular;
  observacionesPendientesPersonas: number;
  preexistenciasDeclaradasPersonas: number;
  coberturaPrivadaDeclaradaPersonas: number;

  // Por estado: personas bloqueadas y legajos con titular bloqueado. El
  // dashboard resta esto de cada paso para mostrar el neto disponible para alta.
  bloqueadosPorEstado: Record<string, { legajos: number; personas: number }>;

  // Por estado: personas de baja (baja micrositio o bajaBinary) y legajos con
  // titular de baja. A diferencia del bloqueo, la baja SÍ se resta de
  // `dado_de_alta` (una persona de baja no es un alta activa). Opcional: si la
  // API es más vieja que la UI viene undefined y se trata como 0.
  bajasPorEstado?: Record<string, { legajos: number; personas: number }>;

  // Reconciliación a nivel persona de los padrones cerrados (cada asociado una
  // vez): total = enEmbudo + soloBis + bloqueados + bajas + huerfanos. Cierra el
  // gap entre el total del embudo (cuenta miembros, excluye bis) y las personas
  // reales en cerrados. `bloqueados`/`bajas` son sin-legajo a propósito (sacados
  // del alta); `huerfanos` queda solo para las anomalías reales de integridad.
  cerradosReconciliacion: {
    total: number;
    enEmbudo: number;
    soloBis: number;
    bloqueados?: number;
    bajas?: number;
    huerfanos: number;
  };

  // Estados a nivel PERSONA (asociado), MUTUAMENTE EXCLUYENTES (prioridad de
  // baja › bloqueada › con error › activa): un dado de baja NO cuenta como
  // bloqueado. `bajaBinary`/`bajaMicrositio` son sub-detalle de `deBaja`.
  // Opcional: si la API es más vieja que la UI, viene undefined y el widget no
  // se muestra.
  estadosPersona?: {
    total: number;
    activas: number;
    bloqueadas: number;
    deBaja: number;
    conError: number;
    bajaBinary: number;
    bajaMicrositio: number;
  };
}

export interface PreexistenciaItem {
  asociadoId: string;
  asociadoNombres: string;
  asociadoApellido: string;
  asociadoParentesco: string;
  codigo: string;
  seccion: string;
  pregunta: string;
  detalle: string;
  detalleExtra: string;
}

export interface LegajoConPreexistencias {
  legajoId: string;
  legajoNumero: string;
  padronId: string;
  empresaId: string;
  titularApellido: string;
  titularNombres: string;
  preexistencias: PreexistenciaItem[];
}

export async function listarPreexistenciasPendientes(): Promise<LegajoConPreexistencias[]> {
  const { data } = await api.get<LegajoConPreexistencias[]>('/legajos/preexistencias-pendientes');
  return data;
}

export interface CoberturaPrivadaItem {
  asociadoId: string;
  asociadoNombres: string;
  asociadoApellido: string;
  asociadoParentesco: string;
  proveedor: string;
  plan: string;
}

export interface LegajoConCoberturaPrivada {
  legajoId: string;
  legajoNumero: string;
  padronId: string;
  empresaId: string;
  titularApellido: string;
  titularNombres: string;
  coberturas: CoberturaPrivadaItem[];
}

export async function listarCoberturaPrivadaPendientes(): Promise<LegajoConCoberturaPrivada[]> {
  const { data } = await api.get<LegajoConCoberturaPrivada[]>(
    '/legajos/cobertura-privada-pendientes',
  );
  return data;
}

/**
 * Descarga un xlsx con los legajos asociados a una métrica del dashboard.
 * `metric` es un identificador estable: 'total', 'estado.<estado>',
 * 'wizard.<paso>', 'validado.<plan>'.
 */
export async function descargarMetricaBlob(
  metric: string,
): Promise<{ filename: string; totalLegajos: number }> {
  const res = await api.get('/legajos/export-metrica', {
    responseType: 'blob',
    params: { metric },
  });
  const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
  const m = cd.match(/filename="?([^"]+)"?/i);
  const filename = m?.[1] ?? `legajos_${metric}.xlsx`;
  const totalLegajos = Number(res.headers['x-metrica-total-legajos'] ?? '0') || 0;
  const blob = new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return { filename, totalLegajos };
}

export async function getDashboardGlobal(opts?: {
  fresh?: boolean;
}): Promise<DashboardLegajosGlobal> {
  const { data } = await api.get<DashboardLegajosGlobal>('/legajos/dashboard-global', {
    params: opts?.fresh ? { fresh: 1 } : undefined,
  });
  return data;
}

/**
 * KPI "Elección del usuario" — cuenta agregada de cuántos titulares
 * eligieron Plan MS1, Plan MS2 o Contacto comercial, juntando
 * `validado_por_titular` + `en_alta_binary`. Solo Medicus.
 */
export interface DesgloseEleccionDelUsuario {
  planMs1: number;
  planMs2: number;
  contactoComercial: number;
  // Conteos paralelos de PERSONAS por cada categoría.
  planMs1Personas: number;
  planMs2Personas: number;
  contactoComercialPersonas: number;
}

export async function getEleccionDelUsuario(opts?: {
  fresh?: boolean;
}): Promise<DesgloseEleccionDelUsuario> {
  const { data } = await api.get<DesgloseEleccionDelUsuario>('/legajos/eleccion-del-usuario', {
    params: opts?.fresh ? { fresh: 1 } : undefined,
  });
  return data;
}

export interface ObservacionPendiente {
  campo: string;
  asociadoId: string;
  asociadoNombres: string;
  asociadoApellido: string;
  asociadoParentesco: string;
  valorActual: string | null;
  valorPropuesto?: string;
  mensaje?: string;
  reportadoAt: string;
}

export interface LegajoConObservaciones {
  legajoId: string;
  legajoNumero: string;
  padronId: string;
  empresaId: string;
  observaciones: ObservacionPendiente[];
}

export async function listarObservacionesPendientes(): Promise<LegajoConObservaciones[]> {
  const { data } = await api.get<LegajoConObservaciones[]>('/legajos/observaciones-pendientes');
  return data;
}

export async function listLegajos(padronId: string): Promise<Legajo[]> {
  const { data } = await api.get<Legajo[]>(`/legajos`, { params: { padronId } });
  return data;
}

export interface DeleteLegajoResult {
  id: string;
  deleted: boolean;
  cascade: {
    asociados: number;
    ddjjRespuestas: number;
    otps: number;
  };
}

export async function deleteLegajo(id: string): Promise<DeleteLegajoResult> {
  const { data } = await api.delete<DeleteLegajoResult>(`/legajos/${id}`);
  return data;
}

export async function validarLegajo(id: string): Promise<{ id: string; estado: 'creado' }> {
  const { data } = await api.post(`/legajos/${id}/validar`);
  return data;
}

export async function rechazarLegajo(
  id: string,
  motivo: string,
): Promise<{ id: string; estado: 'rechazado_medicus'; motivo: string }> {
  const { data } = await api.post(`/legajos/${id}/rechazar`, { motivo });
  return data;
}

export interface ValidarPadronResult {
  id: string;
  estado: 'cerrado';
  legajosValidados: number;
  /** Legajos que ya estaban afiliados en Binary y no recibieron mail. */
  yaAfiliados?: { completos: number; parciales: number };
  /**
   * La organización no usa el asistente del titular: los legajos quedaron
   * directo "listos para alta" y no salió ningún mail.
   */
  sinWizard?: boolean;
  /** Bis del padrón activados sin asistente (solo con `sinWizard`). */
  bisActivados?: number;
}

export async function validarPadron(id: string): Promise<ValidarPadronResult> {
  const { data } = await api.post<ValidarPadronResult>(`/padrones/${id}/validar`);
  return data;
}

/**
 * Descarga un archivo binario usando el axios cliente (que ya conoce la
 * baseURL `${host}/bff-admision`) y dispara la descarga con un blob URL.
 * Más robusto que `<a href>` porque:
 *  - No depende de que `VITE_API_BASE_URL` se haya inyectado en build.
 *  - El browser no puede "navegar" por accidente — siempre descarga.
 *  - El nombre de archivo viene del `Content-Disposition` del server.
 */
async function descargarBlob(path: string, filenameFallback: string): Promise<void> {
  const res = await api.get(path, { responseType: 'blob' });
  const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
  const match = cd.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] ?? filenameFallback;

  const blob = new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function descargarPlantillaBlob(): Promise<void> {
  await descargarBlob('/padrones/plantilla/descargar', 'plantilla_admision_masiva.xlsx');
}

export interface PreviewCierreItem {
  numero: string;
  personas: number;
  legajoExistenteId: string;
  estadoExistente: string;
  padronExistenteId: string;
}

export interface PreviewCierre {
  padronId: string;
  registrosError: number;
  totales: {
    legajosACrear: number;
    legajosAMergear: number;
    legajosBis: number;
    gruposSaltados: number;
    personasAMergear: number;
  };
  merges: PreviewCierreItem[];
  bis: PreviewCierreItem[];
}

/**
 * Análisis previo al cierre del padrón — lo usa el modal para mostrar
 * los merges y avisar de los casos que van a quedar como "Adherentes"
 * (legajo_bis en backend) cuando el número coincide con un legajo en
 * flujo avanzado.
 */
export async function previewCierrePadron(id: string): Promise<PreviewCierre> {
  const { data } = await api.get<PreviewCierre>(`/padrones/${id}/preview-cierre`);
  return data;
}

export interface CerrarPadronResp {
  id: string;
  estado: string;
  mensaje: string;
  legajosCreados?: number;
  legajosMergeados?: number;
  legajosBis?: number;
  personasMergeadas?: number;
}

/**
 * Cierra el padrón. Si hay merges pendientes, el backend devuelve 409 a
 * menos que se pase `confirmarMerges: true` (el flow normal en la UI:
 * primero llamar a `previewCierrePadron`, mostrar modal, y al confirmar
 * llamar acá con `confirmarMerges: true`).
 */
export async function cerrarPadron(
  id: string,
  opts: { confirmarMerges?: boolean } = {},
): Promise<CerrarPadronResp> {
  const { data } = await api.post<CerrarPadronResp>(`/padrones/${id}/cerrar`, {
    confirmarMerges: !!opts.confirmarMerges,
  });
  return data;
}

export interface EliminarPadronResult {
  id: string;
  deleted: true;
  cascade: { asociados: number; legajos: number };
}

export async function eliminarPadron(id: string): Promise<EliminarPadronResult> {
  const { data } = await api.delete<EliminarPadronResult>(`/padrones/${id}`);
  return data;
}

export async function separarInconsistencias(
  id: string,
): Promise<{ padronOriginal: PadronSummary; padronNuevo: PadronSummary }> {
  const { data } = await api.post(`/padrones/${id}/separar-inconsistencias`);
  return data;
}

/**
 * Re-aplica las reglas vigentes (mutaciones + validación row + cross-row)
 * sobre todas las filas del padrón. Útil cuando se cambió alguna regla
 * después del upload original y el operador quiere recuperar filas que
 * antes daban error. Solo aplica a padrones en borrador.
 */
export async function revalidarPadron(
  id: string,
): Promise<PadronSummary & { revalidadas: number; durationMs: number }> {
  const { data } = await api.post(`/padrones/${id}/revalidar`);
  return data;
}

export async function downloadInconsistenciasBlob(id: string): Promise<void> {
  await descargarBlob(`/padrones/${id}/inconsistencias/export`, `inconsistencias-${id}.xlsx`);
}

/**
 * Descarga el padrón completo (todas las filas) como Excel. Disponible
 * cuando el padrón ya está cerrado por el UE (`estado !== 'borrador'`).
 * Para todos los roles que tengan acceso al padrón (UE de su empresa,
 * admins de Medicus en cualquier padrón).
 */
export async function downloadPadronBlob(id: string): Promise<void> {
  await descargarBlob(`/padrones/${id}/export`, `padron-${id}.xlsx`);
}

/**
 * Descarga un Excel con una sola columna `email` y los emails de TODOS
 * los titulares del padrón, deduplicados. Solo admin Medicus / SuperAdmin.
 * El backend rechaza si el padrón está en borrador.
 */
export async function downloadEmailsTitularesBlob(id: string): Promise<void> {
  await descargarBlob(`/padrones/${id}/export-emails-titulares`, `emails-titulares-${id}.xlsx`);
}

export async function updateAsociado(id: string, patch: Partial<Asociado>): Promise<Asociado> {
  const { data } = await api.put<Asociado>(`/asociados/${id}`, patch);
  return data;
}

export async function deleteAsociado(id: string): Promise<void> {
  await api.delete(`/asociados/${id}`);
}

// ============================================================================
// Chunked upload — workaround del 413 que aparece cuando Kong/Ingress nginx
// tiene un body limit bajo (defaults ~1MB) y nuestros padrones pesan 20MB+.
// Cada chunk va por debajo del limit; el server los reensambla en Redis.
// ============================================================================

const CHUNK_SIZE = 500 * 1024; // 500 KB
const CHUNK_THRESHOLD = 500 * 1024; // archivos > 500KB van por chunked
const CHUNK_PARALELO = 4; // 4 chunks simultáneos (acelera sin saturar la red)

function debeChunkear(file: File): boolean {
  return file.size > CHUNK_THRESHOLD;
}

/**
 * Sube `file` partiendo en chunks de 500KB. El último chunk dispara
 * internamente en el server el endpoint target (`upload` o `preview`) y
 * devuelve esa respuesta (mismo contrato que los endpoints originales).
 */
async function uploadChunked<T>(
  file: File,
  target: 'upload' | 'preview',
  contexto?: ContextoCargaPadron,
): Promise<T> {
  // crypto.randomUUID existe en todos los browsers que el proyecto soporta
  // (Chrome 92+, Safari 15.4+, Firefox 95+). El antiguo fallback con
  // Math.random fue removido porque (1) ya no es necesario y (2) Sonar lo
  // flageaba como weak-cryptography aunque el uploadId no sea criptográfico.
  const uploadId = crypto.randomUUID().replace(/[^a-zA-Z0-9-]/g, '');
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  // Mandar todos los chunks excepto el último en paralelo controlado.
  // El último se reserva para serial al final (es el que dispara el
  // procesamiento y queremos su respuesta de cierre).
  const lastIndex = totalChunks - 1;
  const indices = Array.from({ length: lastIndex }, (_, i) => i);
  let next = 0;
  async function worker() {
    while (next < indices.length) {
      const i = indices[next++];
      await postChunk(file, uploadId, i, totalChunks, target, contexto);
    }
  }
  await Promise.all(Array.from({ length: Math.min(CHUNK_PARALELO, indices.length) }, worker));

  // Último chunk (síncrono y devuelve el resultado del endpoint target).
  return postChunk<T>(file, uploadId, lastIndex, totalChunks, target, contexto);
}

async function postChunk<T = { received: number }>(
  file: File,
  uploadId: string,
  chunkIndex: number,
  totalChunks: number,
  target: 'upload' | 'preview',
  contexto?: ContextoCargaPadron,
): Promise<T> {
  const from = chunkIndex * CHUNK_SIZE;
  const to = Math.min(from + CHUNK_SIZE, file.size);
  const blob = file.slice(from, to);
  const form = new FormData();
  form.append('chunk', blob, file.name);
  const ctx = conContexto(contexto);
  const { data } = await api.post<T>('/uploads/chunk', form, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'X-Upload-Id': uploadId,
      'X-Chunk-Index': String(chunkIndex),
      'X-Total-Chunks': String(totalChunks),
      'X-Original-Name': file.name,
      'X-Target': target,
      ...ctx.headers,
    },
    params: ctx.params,
    // El último chunk dispara el procesamiento del padrón (puede tardar 30s+
    // con 200k filas). Subimos el timeout específicamente para ese caso.
    timeout: chunkIndex === totalChunks - 1 ? 5 * 60 * 1000 : 60 * 1000,
  });
  return data;
}
